import { Popover, Table, TableBody, TableCell, TableRow } from '@material-ui/core'
import { Button } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { updateSorting } from 'lib/api'
import { useOrderStore } from 'lib/hooks'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PICKING_SORTING_FIELDS, SortedPickingListItem } from 'shared'
import { CARDTRADER_ZERO_PICKING_LIST_NAME } from 'utils/constants'

// User-selectable sort fields the picking UI offers. Rails is the sole sort authority now, but it
// may hand back internal tiebreakers (pt_order_id/pt_order_article_id) or its bare fallback; those
// are meaningless as a visible sort, so the UI maps anything outside this set to 'none'.
const VALID_SORT_BASE_FIELDS = new Set<string>([
  'product.category',
  'game',
  'extras',
  'releaseDate',
  'product.expansion',
  'product.rarity',
  'product.enName',
  'product.locName',
  'cn',
  'condition',
  'language',
  'comment',
  'price',
  'color',
  'orderBin',
  'location',
  'set',
  'releaseExpCode',
])

export const ArticleSorter = (): JSX.Element => {
  const { t } = useTranslation()
  const { user } = useUser()

  const {
    activePickingList,
    articlesPicked,
    articlesToPick,
    articlesReadyForSorting,
    refetchArticlesToPick,
  } = useOrderStore()

  const defaultSortingFields = [
    'product.category',
    PICKING_SORTING_FIELDS.LOCATION_ASC,
    PICKING_SORTING_FIELDS.PRODUCT_LOC_NAME_ASC,
    PICKING_SORTING_FIELDS.NONE,
    PICKING_SORTING_FIELDS.NONE,
  ]

  // Slot 0 is always product.category; every other slot keeps its field only if it's a valid
  // user-selectable field, otherwise 'none'. Keeps the UI coherent even if Rails returns internal
  // tiebreakers or an unrecognized field as the persisted sort.
  const sanitizeSortingFields = (fields: string[] | undefined): string[] => {
    const source = fields ?? defaultSortingFields
    return defaultSortingFields.map((_, i) => {
      if (i === 0) return 'product.category'
      const field = source[i]
      if (!field) return PICKING_SORTING_FIELDS.NONE
      const base = field.replace('.asc', '').replace('.desc', '')
      return VALID_SORT_BASE_FIELDS.has(base) ? field : PICKING_SORTING_FIELDS.NONE
    })
  }

  const [sortingFields, setSortingFields] = useState<string[]>(() =>
    sanitizeSortingFields(activePickingList?.sortingFields)
  )

  useEffect(() => {
    setSortingFields(sanitizeSortingFields(activePickingList?.sortingFields))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePickingList?.ptId, activePickingList?.sortingFields?.join(',')])

  const [anchorEls, setAnchorEls] = useState<(null | HTMLElement)[]>([null, null, null, null])
  const { enqueueSnackbar } = useSnackbar()

  const hasCardtraderItems =
    articlesToPick.some((item) => item.source === 'Cardtrader') ||
    articlesPicked.some((item) => item.source === 'Cardtrader')

  const hasDuplicateFields = (fields: string[]): boolean => {
    const activeFields = fields.filter((f) => f && f !== PICKING_SORTING_FIELDS.NONE)
    const baseFields = activeFields.map((f) => getBaseField(f))
    return new Set(baseFields).size !== baseFields.length
  }

  const applyNewSort = async (newSortingFields?: string[]) => {
    const newSort = newSortingFields ?? sortingFields

    if (hasDuplicateFields(newSort)) {
      enqueueSnackbar(t('orders.picking.duplicateSortingFieldsError'), { variant: 'warning' })
      return
    }

    // Rails is the sole sort authority: re-fetch first so the store holds the newly sorted order.
    if (refetchArticlesToPick) {
      await refetchArticlesToPick(newSort)
    }

    if (activePickingList) {
      let items: SortedPickingListItem[] | undefined

      if (hasCardtraderItems) {
        // Build the Cardtrader payload from the server-sorted order now in the store: picked first
        // (ordered by pick time), then the to-pick list in the order Rails returned.
        const { articlesToPick: sortedToPick, articlesPicked: currentPicked } =
          useOrderStore.getState()

        const newArticlesPicked = [...currentPicked].sort((a, b) => {
          const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
          const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
          return dateA - dateB
        })

        items = [
          ...newArticlesPicked.map(
            (article) =>
              ({
                source: article.source,
                sourceArticleId: article.sourceArticleId,
                sourceOrderId: article.sourceOrderId,
              } as SortedPickingListItem)
          ),
          ...sortedToPick.map(
            (article) =>
              ({
                source: article.source,
                sourceArticleId: article.sourceArticleId,
                sourceOrderId: article.sourceOrderId,
              } as SortedPickingListItem)
          ),
        ]
      }

      try {
        await updateSorting(activePickingList.ptId, newSort, items)
        if (hasCardtraderItems) {
          enqueueSnackbar(t('orders.picking.updatedCtPickingList'), { variant: 'success' })
        }
      } catch (e: any) {
        if (e.response?.data?.error?.includes('token')) {
          enqueueSnackbar(t('connectCardtrader.noAccountConnected'), { variant: 'error' })
        } else {
          enqueueSnackbar(t('orders.picking.sortingFieldsSaveError'), { variant: 'error' })
        }
      }
    }
  }

  // One-time migration of the legacy per-browser sort.
  //
  // Before this feature the picking sort lived only in localStorage ('orderSortingFields');
  // now it is persisted server-side (picking list record + user default + Cardtrader). On
  // deploy a backend migration sets user.pickingListSortingFields to the default for everyone,
  // so "user still on default" is our signal that nobody has migrated a custom sort yet.
  //
  // Rules (first-device-wins, so multi-device never fights):
  //  - custom localStorage AND user still on default -> this is the first device: persist that
  //    sort (applyNewSort writes PL + user + Cardtrader), so it becomes authoritative everywhere.
  //  - otherwise (not custom, or another device already migrated) -> nothing to persist.
  //  - either way remove the localStorage copy, which makes this run exactly once per device.
  const legacySortMigrationStartedRef = useRef(false)
  useEffect(() => {
    if (legacySortMigrationStartedRef.current) return
    if (!activePickingList || !articlesReadyForSorting) return

    const raw = localStorage.getItem('orderSortingFields')
    if (raw === null) return // nothing stored on this device (new user or already migrated)

    legacySortMigrationStartedRef.current = true

    const migrateLegacySort = async () => {
      let parsed: unknown
      try {
        parsed = JSON.parse(raw)
      } catch {
        localStorage.removeItem('orderSortingFields') // corrupt value, just drop it
        return
      }

      const isValid =
        Array.isArray(parsed) &&
        parsed.length === defaultSortingFields.length &&
        parsed.every((field) => typeof field === 'string')
      const isCustom = isValid && JSON.stringify(parsed) !== JSON.stringify(defaultSortingFields)
      const userStillOnDefault =
        JSON.stringify(user.pickingListSortingFields ?? defaultSortingFields) ===
        JSON.stringify(defaultSortingFields)

      if (isCustom && userStillOnDefault && !hasDuplicateFields(parsed as string[])) {
        setSortingFields(parsed as string[])
        await applyNewSort(parsed as string[]) // persists picking list + user + Cardtrader
      }

      localStorage.removeItem('orderSortingFields')
    }

    migrateLegacySort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePickingList?.ptId, articlesReadyForSorting])

  const getBaseField = (field: string) => field.replace('.asc', '').replace('.desc', '')

  const updateSortingFields = (idx: number, value: string) => {
    // Functional update so rapid consecutive changes each build on the latest state
    // instead of a stale snapshot (otherwise back-to-back selections drop updates).
    setSortingFields((prev) => {
      const newSortingFields = [...prev]
      newSortingFields[idx] = value

      // If setting to 'none', clear all subsequent fields too
      if (value === PICKING_SORTING_FIELDS.NONE) {
        for (let i = idx + 1; i < newSortingFields.length; i++) {
          newSortingFields[i] = PICKING_SORTING_FIELDS.NONE
        }
      }

      return newSortingFields
    })
  }

  const resetSortBy = () => {
    const newSort = [
      'product.category',
      PICKING_SORTING_FIELDS.LOCATION_ASC,
      PICKING_SORTING_FIELDS.PRODUCT_LOC_NAME_ASC,
      PICKING_SORTING_FIELDS.NONE,
      PICKING_SORTING_FIELDS.NONE,
    ]
    setSortingFields(newSort)
    applyNewSort(newSort)
  }

  const handleOpenPopover = (index: number, event: React.MouseEvent<HTMLElement>) => {
    const newAnchors = [...anchorEls]
    newAnchors[index] = event.currentTarget
    setAnchorEls(newAnchors)
  }

  const handleClosePopover = (index: number) => {
    const newAnchors = [...anchorEls]
    newAnchors[index] = null
    setAnchorEls(newAnchors)
  }

  const findTranslation = (index: number) => {
    const entry = Object.entries(PICKING_SORTING_FIELDS).find((f) => f[1] === sortingFields[index])

    if (!entry) {
      return 'Select'
    }

    return t(`orders.picking.sortingFields.${entry[1]}`)
  }

  const renderSortTable = (index: number) => {
    const ascFields = Object.entries(PICKING_SORTING_FIELDS).filter((f) => f[1].endsWith('.asc'))
    const descFields = Object.entries(PICKING_SORTING_FIELDS).filter((f) => f[1].endsWith('.desc'))

    const rows = ascFields
      .map((asc) => {
        const baseKey = asc[1].replace('.asc', '')
        const descSearch = descFields.find((d) => d[1] === `${baseKey}.desc`)
        let desc
        if (descSearch) {
          desc = descSearch
        }

        return { asc, desc }
      })
      .sort((a, b) =>
        t(`orders.picking.sortingFields.${a.asc[1]}`).localeCompare(
          t(`orders.picking.sortingFields.${b.asc[1]}`)
        )
      )

    const extras = PICKING_SORTING_FIELDS.EXTRAS
    const none = PICKING_SORTING_FIELDS.NONE

    return (
      <Table size="small">
        <TableBody>
          <TableRow>
            <TableCell
              onClick={() => {
                updateSortingFields(index + 1, none)
                handleClosePopover(index)
              }}
            >
              <Button data-testid={'none'} fullWidth>
                {t(`orders.picking.sortingFields.${none}`)}
              </Button>
            </TableCell>
            <TableCell
              onClick={() => {
                updateSortingFields(index + 1, extras)
                handleClosePopover(index)
              }}
            >
              <Button data-testid={'extras'} fullWidth>
                {t(`orders.picking.sortingFields.${extras}`)}
              </Button>
            </TableCell>
          </TableRow>

          {rows.map(({ asc, desc }) => (
            <TableRow key={asc[1]}>
              <TableCell
                onClick={() => {
                  updateSortingFields(index + 1, asc[1] || '')
                  handleClosePopover(index)
                }}
              >
                <Button
                  className={
                    sortingFields[index + 1] === asc[1] ? 'picking-table-selected-sort-button' : ''
                  }
                  data-testid={asc[1]}
                  fullWidth
                >
                  {t(`orders.picking.sortingFields.${asc[1]}`)}
                </Button>
              </TableCell>
              <TableCell
                onClick={() => {
                  updateSortingFields(index + 1, desc![1])
                  handleClosePopover(index)
                }}
              >
                <Button
                  className={
                    sortingFields[index + 1] === (desc ? desc[1] : desc)
                      ? 'picking-table-selected-sort-button'
                      : ''
                  }
                  data-testid={desc ? desc[1] : desc}
                  fullWidth
                >
                  {desc ? t(`orders.picking.sortingFields.${desc[1]}`) : ''}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  return (
    <>
      <div className="d-flex flex-wrap gap-2">
        {[1, 2, 3, 4].map((index) => {
          // Disable this dropdown if the previous user-selectable field is 'none'
          const isDisabled = index > 1 && sortingFields[index - 1] === PICKING_SORTING_FIELDS.NONE

          return (
            <div key={index}>
              <Button
                variant="outlined"
                onClick={(e) => handleOpenPopover(index - 1, e)}
                data-testid={`sort-field-${index}`}
                className="button-as-select"
                disabled={isDisabled}
              >
                {findTranslation(index)}
                <i className="ms-3 fa-solid fa-caret-down"></i>
              </Button>
              <Popover
                open={Boolean(anchorEls[index - 1])}
                anchorEl={anchorEls[index - 1]}
                onClose={() => handleClosePopover(index - 1)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                {renderSortTable(index - 1)}
              </Popover>
            </div>
          )
        })}

        <Button
          data-testid="apply-sort"
          onClick={() => applyNewSort()}
          variant="contained"
          tooltip={hasCardtraderItems ? t('orders.picking.applySortOnCt') : undefined}
        >
          {t('orders.picking.applySort')}
        </Button>

        <Button data-testid="reset-sort-by" onClick={resetSortBy} variant="outlined">
          Reset
        </Button>
      </div>

      {activePickingList?.name !== CARDTRADER_ZERO_PICKING_LIST_NAME && hasCardtraderItems && (
        <p className="mt-2 mb-0" data-testid="apply-sort-ct-hint">
          {t('orders.picking.applySortOnCtHint')}
        </p>
      )}
    </>
  )
}
